import { useState } from 'react';
import * as yup from 'yup';
import { fetchAllProposals, createAuthenticatedSearchResponse } from 'actions';
import {
  ProposalEntity,
  SearchEntity,
  ProviderProposalResponseFields,
} from 'v2/models';
import { useDispatch } from 'react-redux';
import {
  getProposalResponseProgressKey,
  loadResponseProgress,
} from 'v2/utils/proposalUtils';
import { PROPOSAL_AFIRMATIVE } from 'components/proposal_types';
import { BLACK_LIST_REGEX, isTextValid } from 'v2/utils/formValidator';
import {
  FEATURE_FLAGS,
  PROVIDER_RESPONSE_PROPOSAL_EMPTY_ERROR,
  PROVIDER_RESPONSE_PROPOSAL_ERROR_CONTACT_INFO,
} from 'utils/constants';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { ApiError, buildApiError } from 'v2/utils/restFactory';
import { createPromptHistoryRelatedEntity } from 'v2/queries/createPromptHistoryRelatedEntity';
import { AxiosError } from 'axios';
import { TAppDispatch as Dispatch } from 'store';
import { useProposalResponseAccepted } from './useProposalResponseAccepted';
import { useFeatureFlags } from 'v2/hooks/useFeatureFlags';

const getSchema = (isCheckSellerResponseEnabled: boolean) =>
  yup.object().shape({
    response: yup
      .string()
      .test({
        name: 'contact_info',
        test: (value) =>
          isTextValid(value || '', [
            BLACK_LIST_REGEX.email,
            BLACK_LIST_REGEX.phoneNumber,
          ]),
        message: PROVIDER_RESPONSE_PROPOSAL_ERROR_CONTACT_INFO,
      })
      .required(PROVIDER_RESPONSE_PROPOSAL_EMPTY_ERROR),
    check_seller_response_status: yup.string().when('response', {
      is: (response: string) => response && isCheckSellerResponseEnabled,
      then: (schema) =>
        schema.required('Please check the response to continue.'),
      otherwise: (schema) => schema.optional(),
    }),
    hide_identity: yup.boolean(),
    answers: yup.array().of(
      yup.object().shape({
        id: yup.number(),
        question: yup.string(),
        response: yup
          .string()
          .test({
            name: 'contact_info',
            test: (value) =>
              isTextValid(value || '', [
                BLACK_LIST_REGEX.email,
                BLACK_LIST_REGEX.phoneNumber,
              ]),
            message: PROVIDER_RESPONSE_PROPOSAL_ERROR_CONTACT_INFO,
          })
          .required(PROVIDER_RESPONSE_PROPOSAL_EMPTY_ERROR),
      })
    ),
    data_files: yup.array(),
    prompt_history_id: yup.number().optional(),
  });

interface OwnProps {
  proposal: ProposalEntity;
  onSettled?: () => void;
}

type useFormProposalResponseProps = OwnProps;

export function useFormProposalResponse({
  proposal,
  onSettled,
}: useFormProposalResponseProps) {
  const search = proposal.search as SearchEntity;
  const dispatch = useDispatch<Dispatch>();
  const { removeResponseKey } = useProposalResponseAccepted({
    proposalId: proposal.id,
    searchId: search.id,
  });

  const [isCheckSellerResponseEnabled] = useFeatureFlags([
    FEATURE_FLAGS.CHECK_SELLER_RESPONSE,
  ]);
  const [isBusy, setIsBusy] = useState(false);
  const [INITIAL_FORM_STATE] = useState(() => {
    const savedResponse = loadResponseProgress(proposal.id, search.id);
    return {
      check_response_status: undefined,
      check_response_feedback: undefined,
      response: savedResponse?.response || '',
      answers: search.questions.map((item, idx) => ({
        id: item.id,
        question: item.name,
        response: savedResponse?.answers[idx].response || '',
      })),
      hide_identity: savedResponse?.hide_identity || false,
      data_files: savedResponse?.data_files || [],
      prompt_history_id: undefined,
    };
  });

  const schema = getSchema(isCheckSellerResponseEnabled);

  const formContext = useForm<ProviderProposalResponseFields>({
    defaultValues: INITIAL_FORM_STATE,
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const onSubmit = async (values: ProviderProposalResponseFields) => {
    try {
      const prompt_history = values.prompt_history_id;
      const response = {
        action: PROPOSAL_AFIRMATIVE,
        response: values.response,
        answers: values.answers.map((item) => ({
          question: item.id,
          response: item.response,
        })),
        hide_identity: values.hide_identity,
        data_files: values.data_files.length
          ? values.data_files.map((item) => item.id)
          : undefined,
      };

      setIsBusy(true);
      const proposalUpdated = await dispatch(
        createAuthenticatedSearchResponse(proposal.id, response)
      );

      const dataProviderResponseId =
        proposalUpdated?.data_provider_response?.id;
      if (prompt_history && dataProviderResponseId !== undefined) {
        createPromptHistoryRelatedEntity({
          app_label: 'user',
          model_name: 'componentsproposalresponses',
          object_id: dataProviderResponseId,
          prompt_history,
        });
      }
      await dispatch(fetchAllProposals(false));
      onSettled ? onSettled() : removeResponseKey();
      toast.success('Response submitted successfully.');
      localStorage.removeItem(
        getProposalResponseProgressKey(proposal.id, search.id)
      );
    } catch (error) {
      toast.error(buildApiError(error as AxiosError<ApiError>));
      await dispatch(fetchAllProposals(false));
      onSettled ? onSettled() : removeResponseKey();
    }
  };

  return {
    formContext,
    onSubmit,
    isBusy,
    isCheckSellerResponseEnabled,
  };
}
